import React, { FC, Fragment, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';

import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import Typography from '@mui/material/Typography';

import {
  useApprove,
  useBalance,
} from '@services/contract/common/erc1155.contract';

import { useAuth } from '@contexts/common/AuthorizationContext';
import { modalActions } from '@contexts/common/ModalContext';

import useBondsInfo from '@hooks/bonds/useBondsInfo';
import useCreateRedeemNft from '@hooks/bonds/useCreateRedeemNft';

import { modifyEvent } from '@helpers/common/form.helpers';

import { formatBNWithCommas, toBN } from '@utils/formaters.utils';

import { amountYup } from '@validators/vaults/common.validators';

import Button from '@components/common/Button';
import Input from '@components/common/Input';
import ModalWrapper from '@components/common/Modal/ModalWrapper';
import SkeletonLoader from '@components/common/SkeletonLoader';

import {
  BONDS_REDEEM_DESCRIPTION,
  BONDS_REDEEM_DISCOUNT,
} from '@constants/bonds/common.constants';
import {
  NFT_BONDS,
  NFT_REDEMPTION,
} from '@constants/bonds/contracts.constants';
import {
  DEFAULT_CRITERIA_MODE,
  DEFAULT_REVALIDATE_MODE,
  FieldNames,
} from '@constants/common/form.constants';
import { ONLY_POSITIVE_INTEGERS } from '@constants/common/regex.constants';
import { Size, Variant } from '@constants/common/ui.constants';

import useStyles from './styles.module';

type FormValues = {
  [FieldNames.Amount]: string;
};

type BondsInfo = NonNullable<ReturnType<typeof useBondsInfo>['data']>;

const RedeemModalContent: FC<{ bondsInfo: BondsInfo }> = ({ bondsInfo }) => {
  const { bondPrice, nativeToken, chainId } = bondsInfo;
  const { classes } = useStyles();
  const { account } = useAuth();

  const {
    data: balance,
    isPending: isPendingBalance,
    refetch: refetchBalance,
  } = useBalance({ nftAddress: NFT_BONDS, chainId });

  const {
    isApproved,
    approve,
    isPendingApprove,
    refetch: refetchApprove,
  } = useApprove({ spender: NFT_REDEMPTION, nftAddress: NFT_BONDS, chainId });

  const handleUpdate = async () => {
    await Promise.all([refetchBalance(), refetchApprove()]);
  };

  const { redeem, isPending: isPendingRedeem } =
    useCreateRedeemNft(handleUpdate);

  const validationSchema = yup.object().shape({
    amount: amountYup(toBN(balance)),
  });

  const {
    control,
    handleSubmit,
    trigger,
    setValue,
    formState: { isValid },
    watch,
    reset,
  } = useForm<FormValues>({
    mode: DEFAULT_CRITERIA_MODE,
    reValidateMode: DEFAULT_REVALIDATE_MODE,
    resolver: yupResolver(validationSchema),
    defaultValues: { [FieldNames.Amount]: '' },
  });

  const redeemValue = toBN(watch(FieldNames.Amount) || 0);

  const onSubmit = async (formData: FormValues) => {
    await trigger();
    await redeem(Number(formData[FieldNames.Amount]));
    reset();
    await refetchBalance();
    modalActions.hide();
  };

  const onMaxHandler = useCallback(() => {
    if (!account || !balance || toBN(balance).isZero()) return;
    setValue(FieldNames.Amount, toBN(balance).toFixed(0), {
      shouldValidate: true,
    });
  }, [balance, account, setValue]);

  const isDisableButton = !balance || toBN(balance).isZero() || !isValid;

  const priceColumns = [
    {
      label: 'Redeem price',
      amount: bondPrice.times(BONDS_REDEEM_DISCOUNT),
      accent: false,
    },
    {
      label: 'Total price',
      amount: isValid
        ? bondPrice.times(BONDS_REDEEM_DISCOUNT).times(redeemValue)
        : null,
      accent: true,
    },
  ];

  return (
    <ModalWrapper title="Redeem Bond">
      <form className={classes.container} onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="body2">{BONDS_REDEEM_DESCRIPTION}</Typography>
        <div className={classes.totalBlock}>
          {priceColumns.map((col, i) => (
            <Fragment key={col.label}>
              {i > 0 && <div className={classes.separator} />}
              <div className={classes.priceColumn}>
                <Typography
                  variant="body2"
                  color={(theme) => theme.palette.custom.text.accent}
                >
                  {col.label}
                </Typography>
                <div className={classes.value}>
                  {col.amount ? (
                    <>
                      <Typography
                        variant="subtitle1"
                        color={(theme) =>
                          col.accent
                            ? theme.palette.custom.text.active
                            : theme.palette.custom.text.dark
                        }
                      >
                        {formatBNWithCommas(
                          col.amount,
                          4,
                          nativeToken.decimals,
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        color={(theme) => theme.palette.custom.text.accent}
                      >
                        {nativeToken.symbol}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="subtitle1">—</Typography>
                  )}
                </div>
              </div>
            </Fragment>
          ))}
        </div>
        <div className={classes.inputContainer}>
          {isPendingBalance ? (
            <SkeletonLoader
              className={classes.skeleton}
              classNameWrap={classes.skeleton}
            />
          ) : (
            <Typography
              variant="caption1"
              color={(theme) => theme.palette.custom.text.accent}
            >
              {`Balance: ${formatBNWithCommas(balance, 0)}`}
            </Typography>
          )}
          <Controller
            control={control}
            name={FieldNames.Amount}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                value={value}
                onBlur={onBlur}
                onChange={(e) =>
                  onChange(modifyEvent(e, ONLY_POSITIVE_INTEGERS))
                }
                placeholder="0"
                onMax={onMaxHandler}
                inputError={error?.message}
                disabled={!account}
              />
            )}
          />
        </div>
        {isApproved ? (
          <Button
            variant={Variant.Contained}
            size={Size.Large}
            disabled={isDisableButton}
            buttonType="submit"
            isLoading={isPendingRedeem}
          >
            Redeem
          </Button>
        ) : (
          <Button
            variant={Variant.Contained}
            size={Size.Large}
            onClick={approve}
            isLoading={isPendingApprove}
          >
            Approve
          </Button>
        )}
      </form>
    </ModalWrapper>
  );
};

const RedeemModal: FC = () => {
  const { data: bondsInfo } = useBondsInfo();

  if (!bondsInfo) return null;

  return <RedeemModalContent bondsInfo={bondsInfo} />;
};

export default RedeemModal;
